/**
 * SiteVisitsPage — List and create site visits for a project.
 *
 * Offline support (10d.2):
 *  - Create visit while offline → stored in IndexedDB offlineQueue
 *  - When back online, sync handler uploads automatically
 *  - Offline queue entries shown alongside API data
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Plus, Calendar, CheckSquare, Image, AlertTriangle, Clock, Thermometer, Wind, Droplets, Loader2 } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { useSiteVisits, useCreateSiteVisit, useProjectWeather, type SiteVisitCreate, type SiteVisit, type WeatherData, type WeatherCondition } from '@/hooks/useSiteVisits';
import { useProjectById } from '@/hooks/useProjects';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useOnlineDetection } from '@/hooks/useOnlineDetection';
import { offlineQueue, type OfflineQueueEntry } from '@/lib/offline/offlineQueue';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDateOnly } from '@/lib/utils';

// Fallback UUID generator for offline
function generateId(): string {
  if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID();
  return 'offline-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

const WEATHER_CONDITIONS: { value: WeatherCondition; label: string }[] = [
  { value: 'clear', label: 'Clear' },
  { value: 'partly_cloudy', label: 'Partly Cloudy' },
  { value: 'cloudy', label: 'Cloudy' },
  { value: 'rain', label: 'Rain' },
  { value: 'heavy_rain', label: 'Heavy Rain' },
  { value: 'snow', label: 'Snow' },
  { value: 'fog', label: 'Fog' },
  { value: 'thunderstorm', label: 'Thunderstorm' },
  { value: 'windy', label: 'Windy' },
];

const VISIT_TYPE_LABELS: Record<string, string> = {
  site_visit_info: 'Site Visit for Information',
  site_review: 'Site Review',
  progress: 'Progress Review',
  safety: 'Safety Review',
  punch_list: 'Punch List',
  other: 'Other',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  submitted: 'bg-purple-100 text-purple-700',
};

interface CreateFormData {
  title: string;
  visit_date: string;
  visited_by: string;
  visit_type: string;
  notes: string;
  weather_notes: string;
  weather_temperature_c: string;
  weather_conditions: WeatherCondition | '';
  recipient: string;
}

const EMPTY_FORM: CreateFormData = {
  title: '',
  visit_date: format(new Date(), 'yyyy-MM-dd'),
  visited_by: '',
  visit_type: 'site_review',
  notes: '',
  weather_notes: '',
  weather_temperature_c: '',
  weather_conditions: '',
  recipient: '',
};

function getDefaultVisitType(workflowStage: string | undefined): string {
  if (!workflowStage) return 'site_visit_info';
  const constructionStages = ['construction_administration'];
  const preConstructionStages = ['project_setup', 'design_development', 'permit_preparation', 'in_permit_review'];
  if (constructionStages.includes(workflowStage)) return 'site_review';
  if (preConstructionStages.includes(workflowStage)) return 'site_visit_info';
  return 'site_visit_info';
}

export function SiteVisitsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isOnline } = useOnlineDetection();
  const { syncState } = useOfflineSync();
  const queryClient = useQueryClient();

  const { data: visits = [], isLoading } = useSiteVisits(projectId);
  const { data: project } = useProjectById(projectId);
  const createVisit = useCreateSiteVisit();

  // Read URL params synchronously for reliable pre-fill (lazy init avoids useEffect timing issues)
  const _paramDeliverableId = searchParams.get('deliverable_id');
  const _paramTitle = searchParams.get('title');
  const _paramVisitType = searchParams.get('visit_type');

  const [showCreate, setShowCreate] = useState(() => !!_paramDeliverableId);
  const [linkedDeliverableId, setLinkedDeliverableId] = useState<string | null>(_paramDeliverableId);

  const { data: fetchedWeather, isLoading: isWeatherLoading } = useProjectWeather(
    showCreate ? projectId : null
  );
  const [form, setForm] = useState<CreateFormData>(() => ({
    ...EMPTY_FORM,
    title: _paramTitle ? decodeURIComponent(_paramTitle) : EMPTY_FORM.title,
    visit_type: _paramVisitType || EMPTY_FORM.visit_type,
  }));

  // Pre-populate weather fields when weather loads and form is fresh
  useEffect(() => {
    if (fetchedWeather && form.weather_conditions === '' && form.weather_temperature_c === '') {
      setForm((f) => ({
        ...f,
        weather_conditions: fetchedWeather.conditions,
        weather_temperature_c: fetchedWeather.temperature_c !== null ? String(fetchedWeather.temperature_c) : '',
      }));
    }
  }, [fetchedWeather]); // eslint-disable-line react-hooks/exhaustive-deps
  const [isSaving, setIsSaving] = useState(false);
  const [offlineVisits, setOfflineVisits] = useState<OfflineQueueEntry[]>([]);

  useEffect(() => {
    offlineQueue.getPending().then((entries) => {
      setOfflineVisits(entries.filter((e) => e.action === 'create_visit'));
    });
  }, [syncState.pendingCount]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!projectId) return;
    if (!form.title.trim()) return;

    setIsSaving(true);

    // Build weather_data from form fields when at least conditions is set
    let weather_data: WeatherData | undefined;
    if (form.weather_conditions) {
      weather_data = {
        conditions: form.weather_conditions as WeatherCondition,
        temperature_c: form.weather_temperature_c !== '' ? parseFloat(form.weather_temperature_c) : null,
        humidity_pct: fetchedWeather?.humidity_pct ?? null,
        wind_kph: fetchedWeather?.wind_kph ?? null,
        source: fetchedWeather ? 'open-meteo' : 'manual',
      };
    }

    const payload: SiteVisitCreate = {
      title: form.title.trim(),
      visit_date: form.visit_date,
      visited_by: form.visited_by.trim() || undefined,
      visit_type: form.visit_type as SiteVisit['visit_type'],
      notes: form.notes.trim() || undefined,
      weather_notes: form.weather_notes.trim() || undefined,
      weather_data,
      recipient: form.recipient.trim() || undefined,
      deliverable_id: linkedDeliverableId || undefined,
    };

    if (isOnline) {
      createVisit.mutate(
        { projectId, visit: payload },
        {
          onSuccess: () => {
            setShowCreate(false);
            setForm({ ...EMPTY_FORM });
            setLinkedDeliverableId(null);
          },
        }
      );
    } else {
      // Queue offline
      const localId = generateId();
      await offlineQueue.add({
        id: localId,
        action: 'create_visit',
        project_id: projectId,
        entity_id: localId,
        visit_id: null,
        data: payload,
        photo_ids: [],
        offline_timestamp: Date.now(),
        status: 'pending',
      });
      toast.success('Saved offline — will sync when back online');
      setShowCreate(false);
      setForm({ ...EMPTY_FORM });
      // Invalidate so offline items rerender if we add offline-queue to the list
      queryClient.invalidateQueries({ queryKey: ['site-visits', projectId] });
    }

    setIsSaving(false);
  };

  return (
    <DashboardLayout title="Site Visits">
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Offline notice */}
        {!isOnline && (
          <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            You're offline. New site visits will be saved locally and synced when you're back online.
          </div>
        )}

        {/* Pending offline count */}
        {syncState.pendingCount > 0 && isOnline && (
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            <Clock className="h-4 w-4 flex-shrink-0" />
            {syncState.isSyncing
              ? `Syncing ${syncState.pendingCount} offline item(s)…`
              : `${syncState.pendingCount} item(s) pending sync`}
          </div>
        )}

        {/* Header actions */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/projects/${projectId}`)}
          >
            ← Back to Project
          </Button>
          <Button onClick={() => {
            setForm((f) => ({ ...f, visit_type: getDefaultVisitType(project?.workflow_stage) }));
            setShowCreate(true);
          }} className="gap-2">
            <Plus className="h-4 w-4" />
            New Site Visit
          </Button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <h2 className="mb-4 text-base font-semibold">New Site Visit</h2>
            {linkedDeliverableId && _paramTitle && (
              <div className="flex items-center gap-2 rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-sm text-violet-800 mb-3">
                <span className="font-medium">Fulfills deliverable:</span>
                <span className="truncate">{decodeURIComponent(_paramTitle)}</span>
              </div>
            )}
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Framing Site Review"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date *</label>
                  <input
                    type="date"
                    value={form.visit_date}
                    onChange={(e) => setForm((f) => ({ ...f, visit_date: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Conducted By <span className="font-normal text-gray-400">(optional)</span></label>
                  <input
                    type="text"
                    value={form.visited_by}
                    onChange={(e) => setForm((f) => ({ ...f, visited_by: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Name or email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={form.visit_type}
                    onChange={(e) => setForm((f) => ({ ...f, visit_type: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {Object.entries(VISIT_TYPE_LABELS).map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Optional notes…"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Send To <span className="font-normal text-gray-400">(optional)</span></label>
                <input
                  type="text"
                  value={form.recipient}
                  onChange={(e) => setForm((f) => ({ ...f, recipient: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Contractor name, email, or building department"
                />
              </div>

              {/* Weather auto-fill */}
              <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-600">Weather at Site</span>
                  {isWeatherLoading && (
                    <span className="text-xs text-gray-400">Fetching weather…</span>
                  )}
                  {fetchedWeather && !isWeatherLoading && (
                    <span className="flex items-center gap-2 text-xs text-gray-500">
                      {fetchedWeather.temperature_c !== null && (
                        <span className="inline-flex items-center gap-1 rounded bg-blue-50 border border-blue-200 px-1.5 py-0.5 text-blue-700">
                          <Thermometer className="w-3 h-3" />
                          {fetchedWeather.temperature_c}°C
                        </span>
                      )}
                      {fetchedWeather.humidity_pct !== null && (
                        <span className="inline-flex items-center gap-1 rounded bg-teal-50 border border-teal-200 px-1.5 py-0.5 text-teal-700">
                          <Droplets className="w-3 h-3" />
                          {fetchedWeather.humidity_pct}%
                        </span>
                      )}
                      {fetchedWeather.wind_kph !== null && (
                        <span className="inline-flex items-center gap-1 rounded bg-gray-100 border border-gray-200 px-1.5 py-0.5 text-gray-600">
                          <Wind className="w-3 h-3" />
                          {fetchedWeather.wind_kph} km/h
                        </span>
                      )}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Conditions</label>
                    <select
                      value={form.weather_conditions}
                      onChange={(e) => setForm((f) => ({ ...f, weather_conditions: e.target.value as WeatherCondition | '' }))}
                      className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">— not set —</option>
                      {WEATHER_CONDITIONS.map(({ value, label }) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Temperature (°C)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={form.weather_temperature_c}
                      onChange={(e) => setForm((f) => ({ ...f, weather_temperature_c: e.target.value }))}
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="e.g. 15.0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600">Weather Notes</label>
                  <input
                    type="text"
                    value={form.weather_notes}
                    onChange={(e) => setForm((f) => ({ ...f, weather_notes: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g. Light breeze, overcast by noon…"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSaving || createVisit.isPending}>
                  {isSaving || createVisit.isPending ? 'Saving…' : isOnline ? 'Create Visit' : 'Save Offline'}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Visit list */}
        {isLoading ? (
          <div className="py-8 flex items-center justify-center text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading site visits…
          </div>
        ) : visits.length === 0 && offlineVisits.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No site visits logged"
            description="Document field conditions, track issues, generate reports"
            actionLabel="Log Site Visit"
            onAction={() => {
              setForm((f) => ({ ...f, visit_type: getDefaultVisitType(project?.workflow_stage) }));
              setShowCreate(true);
            }}
          />
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[360px] space-y-2">
              {/* Offline-queued visits — show before sync with Offline badge */}
              {offlineVisits.map((entry) => {
                const data = entry.data as { title?: string; visit_date?: string; visited_by?: string; visit_type?: string };
                return (
                  <div
                    key={entry.id}
                    className="rounded-lg border border-dashed border-yellow-300 bg-yellow-50/50 p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium truncate">{data.title ?? 'Unnamed Visit'}</h3>
                        <p className="mt-0.5 text-sm text-gray-500 truncate">
                          {data.visit_date ? formatDateOnly(data.visit_date) : '—'} · {data.visited_by ?? '—'} ·{' '}
                          {VISIT_TYPE_LABELS[data.visit_type ?? ''] ?? data.visit_type ?? '—'}
                        </p>
                      </div>
                      <span className="flex-shrink-0 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                        Offline
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-gray-400">Will sync when back online</p>
                  </div>
                );
              })}

              {visits.map((visit) => (
                <Link
                  key={visit.id}
                  to={`/site-visits/${visit.id}`}
                  className="block rounded-lg border bg-card p-4 shadow-sm transition-colors hover:border-purple-300"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium truncate">{visit.title}</h3>
                      <p className="mt-0.5 text-sm text-gray-500 truncate">
                        {formatDateOnly(visit.visit_date)} · {visit.visited_by} ·{' '}
                        {VISIT_TYPE_LABELS[visit.visit_type] ?? visit.visit_type}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium whitespace-nowrap ${STATUS_COLORS[visit.status] ?? 'bg-gray-100 text-gray-700'}`}>
                        {visit.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Image className="h-3 w-3" />
                      {visit.photo_count} photo{visit.photo_count !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckSquare className="h-3 w-3" />
                      {visit.checklist_item_count} item{visit.checklist_item_count !== 1 ? 's' : ''}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default SiteVisitsPage;
